/**
 * Registration page for new accounts
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AgencyRegisterForm, RegisterForm } from '../../components/auth';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

type RegisterAccountType = 'caregiver' | 'guardian';
type RegisterPageMode = 'standard' | 'agency';

interface RegisterProps {
  mode?: RegisterPageMode;
}

function RegistrationBrandView({ showCompanion = false }: { showCompanion?: boolean }) {
  if (showCompanion) {
    return (
      <div className="flex items-center justify-center">
        <img
          src="/carecal-lilli-companion-logo.png"
          alt="CareCal Lilli Companion"
          className="h-20 w-auto max-w-full"
        />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <img src="/logo.png" alt="CareCal" className="h-10 w-auto" />
    </div>
  );
}

export default function Register({ mode = 'standard' }: RegisterProps) {
  const [accountType, setAccountType] = useState<RegisterAccountType>('caregiver');
  const isAgencyMode = mode === 'agency';

  const handleAccountTypeChange = (value: string) => {
    setAccountType(value as RegisterAccountType);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-linen px-4 py-8">
      <Card className="w-full max-w-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-7">
          <div className="flex justify-center mb-8">
            <RegistrationBrandView showCompanion={isAgencyMode} />
          </div>
          <h1
            className={`font-display font-bold text-blueviolet mb-2 ${
              isAgencyMode ? 'text-2xl' : 'text-3xl'
            }`}
          >
            {isAgencyMode ? 'Create Your Agency Account' : 'Create Your Account'}
          </h1>
          <p className="font-body text-night-500">
            {isAgencyMode
              ? 'Set up agency access to CareCal companion resources'
              : 'Join CareCal and start sharing your availability'}
          </p>
        </div>

        {isAgencyMode ? (
          <div className="overflow-hidden rounded-xl border border-lilac-900 bg-lilac-100 shadow-xs">
            <div className="p-6 sm:p-7">
              <AgencyRegisterForm />

              <div className="mt-8 text-center">
                <p className="font-body text-sm text-night-500">
                  Already have an account?{' '}
                  <Link
                    to="/auth/login?mode=supervisor"
                    className="font-medium text-blueviolet hover:text-blueviolet-light underline"
                  >
                    Log in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 text-center">
              <p className="font-body text-sm font-semibold text-night-700">I'm joining as a...</p>
            </div>

            <div className="overflow-hidden rounded-xl border border-lilac-900 bg-lilac-100 shadow-xs">
              <Tabs value={accountType} onValueChange={handleAccountTypeChange} className="w-full">
                <TabsList className="mx-4 mt-4 grid h-11 w-auto grid-cols-2 items-stretch rounded-lg border border-lilac-900 bg-lilac-500 p-1 text-night-700 sm:mx-5">
                  <TabsTrigger
                    value="caregiver"
                    className="h-full min-h-0 rounded-md px-2 py-0 text-sm font-semibold uppercase text-night-700 data-[state=active]:bg-white data-[state=active]:text-blueviolet data-[state=active]:shadow-none"
                  >
                    Caregiver
                  </TabsTrigger>
                  <TabsTrigger
                    value="guardian"
                    className="h-full min-h-0 rounded-md px-2 py-0 text-sm font-semibold uppercase text-night-700 data-[state=active]:bg-white data-[state=active]:text-blueviolet data-[state=active]:shadow-none"
                  >
                    Guardian
                  </TabsTrigger>
                </TabsList>

                <div className="p-6 pt-5 sm:p-7 sm:pt-6">
                  <TabsContent value="caregiver" className="mt-0">
                    {accountType === 'caregiver' && <RegisterForm accountType="caregiver" />}
                  </TabsContent>
                  <TabsContent value="guardian" className="mt-0">
                    {accountType === 'guardian' && <RegisterForm accountType="guardian" />}
                  </TabsContent>

                  {/* Login Link */}
                  <div className="mt-8 text-center">
                    <p className="font-body text-sm text-night-500">
                      Already have an account?{' '}
                      <Link
                        to="/auth/login"
                        className="font-medium text-blueviolet hover:text-blueviolet-light underline"
                      >
                        Log in
                      </Link>
                    </p>
                  </div>
                </div>
              </Tabs>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
